import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Menu } from "@msuite/ui/menu";
import { ConfirmModal, ModalWrapper, useAsyncModal } from "@msuite/ui/modal";
import { useQueryClient } from "@msuite/ui/query";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import type { ProjectCardWithInvoices } from "@msuite/utils/types";
import React from "react";
import { useNavigate } from "react-router";
import { useCitadel } from "@/hooks/use-citadel";
import { EditProjectModal } from "@/modals/projects/edit-project-modal";

export function RowActions({ cell }: CellContext<ProjectCardWithInvoices, "actions">) {
	/** Context */
	const { queryKey } = useDataTableContext();

	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const editProjectModal = useDisclosure();
	const projectId = cell.row.original.id;
	const asyncModal = useAsyncModal();
	const navigate = useNavigate();

	/** Query */
	const { citadel } = useCitadel();

	/** Functions */
	async function handleOnCompleteEdit() {
		if (!queryKey) return;
		await qc.invalidateQueries({ queryKey });
	}

	function handleOnNavigateToProject() {
		navigate(`/projects/${projectId}`);
	}

	/** Computed */
	// The rule binds against the row, because `Project` reaches the whole organisation, the sites
	// the caller runs, or the ones they created. Same three-state guard as `Can` documents: the
	// entry disappears on a definitive denial only.
	const updateIsDenied = !!citadel && citadel.cannot("update", "Project", { ...cell.row.original });

	/** Render */
	return (
		<React.Fragment>
			<RowActionsWrapper to={`/projects/${projectId}`}>
				<Menu
					items={() => [
						{
							label: t("general.open"),
							icon: icon("Info"),
							onClick: handleOnNavigateToProject,
							cyId: "row-actions-open-button",
						},
						updateIsDenied
							? null
							: {
									label: t("projects.edit_project"),
									onClick: editProjectModal.onOpen,
									icon: icon("Edit"),
								},
					]}
				>
					<Button
						size="xxs"
						icon={<Icon name="EllipsisVertical" />}
						variant="ghost"
						data-cy="row-actions-button"
					/>
				</Menu>
			</RowActionsWrapper>
			<ModalWrapper>
				{editProjectModal.isOpen && (
					<EditProjectModal
						isOpen={editProjectModal.isOpen}
						onClose={editProjectModal.onClose}
						projectId={projectId}
						onComplete={handleOnCompleteEdit}
					/>
				)}
				{asyncModal.isOpen && (
					<ConfirmModal
						controller={asyncModal}
						message={t("confirmation.confirm_deactivation")}
					/>
				)}
			</ModalWrapper>
		</React.Fragment>
	);
}
